'use client'
import { useCallback, useEffect, useRef } from 'react'
import { useDebouncedCallback } from 'ui/hooks'
import type { OrderDTO, OrderShippingAdditionalDataDTO } from 'ecosystem'
import { useCheckoutContext } from '../../../hooks'

interface SyncInput {
  defaultDetails: NonNullable<OrderShippingAdditionalDataDTO['defaultDetails']>
  shippingCost?: number
}

/**
 * Debounce-pushes the FE's current delivery selection to the persisted
 * UPFRONT SF order via `updateOrderDefaultShipment`. The hook mirrors
 * `useDebouncedDraftSync` (used by the invoice DRAFT flow): a 350 ms
 * window keeps the BE from getting hammered as the shopper toggles
 * delivery/pickup or edits an address.
 *
 * Returns `flush` so the parent component's submit / pay handler can
 * await any pending update before handing control off to the payment
 * widget — otherwise the BE order can lag behind the FE state at
 * checkout time.
 */
export const useDebouncedDefaultShipmentSync = (
  input: SyncInput | undefined,
  enabled: boolean,
  delayMs = 350
) => {
  const { updateOrderDefaultShipment } = useCheckoutContext()
  const pendingRef = useRef<Promise<OrderDTO | null> | null>(null)

  const sync = useDebouncedCallback((next: SyncInput) => {
    pendingRef.current = updateOrderDefaultShipment(next)
  }, delayMs)

  const serialised = input ? JSON.stringify(input) : ''

  useEffect(() => {
    if (!enabled || !input) return
    sync(input)
    // `serialised` is the change signal; `input` is intentionally excluded
    // to avoid running on every fresh object identity from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- input identity changes every render; serialised is the value-equality signal
  }, [serialised, enabled, sync])

  // Flush any pending call on unmount so we don't drop the last edit.
  useEffect(() => () => sync.flush?.(), [sync])

  const flush = useCallback(async () => {
    sync.flush?.()
    if (pendingRef.current) await pendingRef.current
  }, [sync])

  return { flush }
}
